import { useEffect, useRef, useState, type FocusEvent, type RefObject } from 'react';

const OPEN_DELAY = 120;
const CLOSE_DELAY = 220;
const RECHECK = 200;
// Menu, popover e diálogo abertos a partir da sidebar vivem num portal, fora
// dela: enquanto o gatilho estiver aberto, ela não fecha.
const HOLD_SELECTOR = '[data-state="open"], [data-sidebar-hold]';

/**
 * Abertura da sidebar desafixada. Hover com intenção — passar pelo trilho a
 * caminho de outra coisa não abre, sair por um instante não fecha — e foco de
 * teclado, para quem navega com Tab.
 */
export function useSidebarHover(asideRef: RefObject<HTMLElement | null>) {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const pointer = useRef({ x: -1, y: -1 });

  useEffect(() => {
    const track = (event: PointerEvent) => {
      pointer.current = { x: event.clientX, y: event.clientY };
    };
    window.addEventListener('pointermove', track, { passive: true });
    return () => {
      window.removeEventListener('pointermove', track);
      window.clearTimeout(timer.current);
    };
  }, []);

  const held = () => Boolean(asideRef.current?.querySelector(HOLD_SELECTOR));

  const pointerInside = () => {
    const rect = asideRef.current?.getBoundingClientRect();
    if (!rect) return false;
    const { x, y } = pointer.current;
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  };

  const onMouseEnter = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setHovered(true), OPEN_DELAY);
  };

  const onMouseLeave = () => {
    window.clearTimeout(timer.current);
    let wasHeld = false;
    const close = () => {
      if (held()) {
        wasHeld = true;
        timer.current = window.setTimeout(close, RECHECK);
        return;
      }
      // Menu modal do Radix tira o pointer-events do body e dispara um mouseleave
      // falso; quando fecha, o ponteiro pode continuar em cima da sidebar. Só
      // nesse caso vale a posição: saindo para um iframe (editor de template), o
      // último pointermove ficou na borda da sidebar e a prenderia aberta.
      if (wasHeld && pointerInside()) {
        timer.current = window.setTimeout(close, RECHECK);
        return;
      }
      setHovered(false);
    };
    timer.current = window.setTimeout(close, CLOSE_DELAY);
  };

  const onFocus = (event: FocusEvent<HTMLElement>) => {
    // Só foco de teclado: o clique também foca o link e deixaria a sidebar presa aberta.
    if (event.target.matches(':focus-visible')) setFocused(true);
  };

  const onBlur = (event: FocusEvent<HTMLElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
    if (held()) return;
    setFocused(false);
  };

  return { open: hovered || focused, handlers: { onMouseEnter, onMouseLeave, onFocus, onBlur } };
}
